import { FilterViewType } from "@smeetz/ui-components";
import type { SearchTableConfig } from "@smeetz/ui-components";

import i18n from "@/lang";

// Narrow cast through `unknown` to bypass TS2589 from vue-i18n v11 generics.
const t: (key: string) => string = (
  i18n.global as unknown as {
    t: (key: string) => string;
  }
).t;

type Options = {
  showTeamsColumn: boolean;
  showCheckInColumn: boolean;
};

/**
 * Build the SearchTableConfig consumed by `<SSearchTable>` in the orders
 * modal. Parameterised by `showTeamsColumn` so the Teams column is hidden by
 * default outside Fort Boyard / dev / test groups (the user can still flip it
 * via SViewConfig).
 *
 * `showCheckInColumn` follows the check-in feature flag.
 *
 * @param {Options} opts - Group-driven flags.
 * @returns {SearchTableConfig} Config ready to pass to SSearchTable.
 */
export function buildOrdersSearchTableConfig(opts: Options): SearchTableConfig {
  return {
    id: "pos.orders",
    collection: "booking",
    searchFields: [
      "reference",
      "customer_full_name",
      "customer_email",
      "seller_full_name",
    ],
    itemsLabelKey: "contents.orders",
    selectable: false,
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],

    tabs: [
      {
        id: "all",
        name: "orders.orders",
        type: FilterViewType.Everyone,
        default: true,
        position: 1.01,
        defaultSort: { field: "created_at", direction: "desc" },
        filters: [
          [
            { key: "booking_state", option: 10, value: [3], isDefault: true },
            {
              key: "request_to_book_status",
              option: 10,
              value: [0],
              isDefault: true,
            },
          ],
        ],
        quickFilters: [
          // Both chips split orders by balance, the way the BO lists do:
          // booking_payment_status can read PAID while a balance is still due.
          // They share a group, so with both on the table shows either kind.
          {
            id: "qf.paid",
            name: t("orders.paid"),
            removable: false,
            isActive: false,
            group: "payment",
            filters: [
              // balance = 0 (the number filter adds 0.001 to `to`)
              {
                key: "balance_value",
                option: 8,
                value: { from: -0.001, to: 0 },
              },
            ],
          },
          {
            id: "qf.with-balance",
            name: t("orders.non-paid"),
            removable: false,
            isActive: false,
            group: "payment",
            filters: [
              // balance ≠ 0, same filter as BO "Commandes avec solde"
              {
                key: "balance_value",
                option: 9,
                value: { from: -0.001, to: 0 },
              },
            ],
          },
        ],
      },
    ],

    columns: {
      reference: {
        localeKey: "orders.booking-reference",
        show: true,
        position: 1,
        "min-width": "200",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "booking",
          modelKeyLabel: "reference",
          modelKeyValue: "reference",
          searchFields: ["reference"],
          groupBy: "reference",
        },
        cell: { type: "default", prop: "reference", sortable: true },
      },
      customer_full_name: {
        localeKey: "orders.customer-full-name",
        show: true,
        position: 2,
        "min-width": "220",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "booking",
          modelKeyLabel: "customerFullName",
          modelKeyValue: "customerFullName",
          searchFields: ["customer_full_name"],
          groupBy: "customer_full_name",
        },
        cell: { type: "default", prop: "customerFullName", sortable: true },
      },
      booking_first_start_date: {
        localeKey: "filters.date-of-visit",
        show: true,
        position: 3,
        "min-width": "290",
        type: "date",
        cell: {
          type: "default",
          prop: "bookingFirstStartDateString",
          sortable: true,
        },
      },
      booking_start_dates_quantity: {
        localeKey: "orders.quantity",
        show: true,
        position: 4,
        "min-width": "100",
        type: "number",
        cell: {
          type: "default",
          prop: "bookingStartDatesQuantity",
          textAlign: "right",
          sortable: true,
        },
      },
      booking_tickets: {
        localeKey: "completeorder.tickets",
        show: true,
        position: 5,
        "min-width": "120",
        cell: { type: "default", prop: "bookingTickets", sortable: true },
      },
      bookings_ticket_quantity: {
        localeKey: "orders.retail-product",
        show: true,
        position: 6,
        "min-width": "150",
        cell: {
          type: "default",
          prop: "bookingsTicketQuantity",
          sortable: true,
        },
      },
      addons: {
        localeKey: "orders.addons",
        show: true,
        position: 7,
        "min-width": "120",
        cell: { type: "default", prop: "id" },
      },
      ...(opts.showCheckInColumn
        ? {
            check_in: {
              localeKey: "check-in.column-checked-in",
              show: true,
              readOnly: true,
              position: 7.5,
              "min-width": "140",
              cell: { type: "default", prop: "id" },
            },
          }
        : {}),
      created_at: {
        localeKey: "orders.creation-date",
        show: true,
        position: 8,
        "min-width": "200",
        type: "date",
        cell: { type: "default", prop: "createdAtString", sortable: true },
      },
      booking_payment_status: {
        localeKey: "orders.payment-status",
        show: true,
        position: 9,
        "min-width": "200",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "booking",
          modelKeyLabel: "bookingPaymentStatusString",
          modelKeyValue: "bookingPaymentStatus",
          searchFields: ["booking_payment_status"],
          groupBy: "booking_payment_status",
        },
        cell: {
          type: "default",
          prop: "bookingPaymentStatusString",
          sortable: true,
        },
      },
      seller_full_name: {
        localeKey: "orders.seller",
        show: true,
        position: 10,
        "min-width": "120",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "booking",
          modelKeyLabel: "sellerFullName",
          modelKeyValue: "sellerFullName",
          searchFields: ["seller_full_name"],
          groupBy: "seller_full_name",
        },
        cell: { type: "default", prop: "sellerFullName", sortable: true },
      },
      booking_channel: {
        localeKey: "orders.booking-channel",
        show: true,
        position: 11,
        "min-width": "120",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "booking",
          modelKeyLabel: "bookingChannel",
          modelKeyValue: "bookingChannel",
          searchFields: ["booking_channel"],
          groupBy: "booking_channel",
        },
        cell: { type: "default", prop: "bookingChannel", sortable: true },
      },
      total_value: {
        localeKey: "orders.total-price",
        show: true,
        position: 12,
        "min-width": "120",
        type: "number",
        displayType: "money-cents",
        cell: {
          type: "default",
          prop: "totalValueString",
          textAlign: "right",
          sortable: true,
        },
      },
      ...(opts.showTeamsColumn
        ? {
            booking_team: {
              localeKey: "completeorder.teams",
              show: true,
              position: 13,
              "min-width": "120",
              type: "select-autocomplete",
              autocomplete: {
                engineKey: "booking",
                modelKeyLabel: "bookingTeam",
                modelKeyValue: "bookingTeam",
                searchFields: ["booking_team"],
                groupBy: "booking_team",
              },
              cell: { type: "default", prop: "bookingTeam", sortable: true },
            },
          }
        : {}),
      actions: {
        localeKey: "cells.actions",
        show: true,
        readOnly: true,
        position: 99,
        "min-width": "240",
        pinned: "right",
        cell: { type: "default", prop: "id" },
      },

      id: { internalUsage: true },
      currency: { internalUsage: true },
      booking_products: { internalUsage: true },
      booking_first_quantity_main: { internalUsage: true },
      ...(opts.showTeamsColumn
        ? { booking_team_setting: { internalUsage: true } }
        : {}),
    },
  };
}
